const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1/api';

function companyHeader() {
  try {
    const id = localStorage.getItem('crmActiveCompanyId');
    return id ? { 'X-Company-Id': id } : {};
  } catch {
    return {};
  }
}

export { companyHeader, API_BASE_URL };

export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('authToken');

  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...companyHeader(),
    ...(options.headers || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const message =
      body?.data?.message ||
      (typeof body?.message === 'string' ? body.message : null) ||
      'Une erreur est survenue. Réessayez.';
    throw new Error(message);
  }

  return body;
}

export async function fetchProspectStats() {
  return apiFetch('/prospects/stats/by-status');
}

export async function fetchProspectFilterMeta() {
  return apiFetch('/prospects/filter-meta');
}

export async function addProspectCustomFieldDefinition(label) {
  return apiFetch('/prospects/custom-field-definitions', {
    method: 'POST',
    body: JSON.stringify({ label: String(label || '').trim() }),
  });
}

export async function fetchProspectCountByTags(tagNames) {
  const tags = Array.isArray(tagNames)
    ? tagNames.map((t) => String(t).trim()).filter(Boolean)
    : [];
  if (tags.length === 0) {
    return { success: true, data: { count: 0 } };
  }
  const params = new URLSearchParams();
  tags.forEach((t) => params.append('tags', t));
  return apiFetch(`/prospects/count-by-tags?${params.toString()}`);
}

export async function fetchProspects(filters = {}) {
  const params = new URLSearchParams();

  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.priority && filters.priority !== 'all') params.set('priority', filters.priority);
  if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
  if (filters.assignmentState) params.set('assignmentState', filters.assignmentState);
  if (filters.search) params.set('search', filters.search);
  if (filters.hasCallback) params.set('hasCallback', 'true');
  if (filters.company && filters.company !== 'all') params.set('company', filters.company);
  if (filters.tags && filters.tags.length > 0) {
    filters.tags.forEach((t) => params.append('tags', t));
  }
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const query = params.toString() ? `?${params.toString()}` : '';

  return apiFetch(`/prospects${query}`);
}

export async function fetchProspectById(id) {
  return apiFetch(`/prospects/${id}`);
}

export async function createProspect(data) {
  return apiFetch('/prospects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProspect(id, data) {
  return apiFetch(`/prospects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function importProspects(rows, mapping, tagsForAll = [], requiredFields = []) {
  return apiFetch('/prospects/import', {
    method: 'POST',
    body: JSON.stringify({ rows, mapping, tagsForAll, requiredFields }),
  });
}

export async function fetchEntrepriseImportJob() {
  const body = await apiFetch('/prospects/entreprise-import/active');
  return body?.data?.job ?? { status: 'idle' };
}

export async function startEntrepriseImportJob(payload) {
  const body = await apiFetch('/prospects/entreprise-import/start', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return body?.data?.job ?? null;
}

export async function fetchEntrepriseSearch({
  codePostal,
  activitePrincipale = '',
  ape = '',
  q = '',
  page = 1,
  perPage = 25,
  nameFilter = false,
} = {}) {
  const params = new URLSearchParams();
  params.set('codePostal', String(codePostal || '').trim());
  const apeVal = activitePrincipale || ape;
  if (apeVal) params.set('ape', String(apeVal).trim());
  if (q) params.set('q', String(q).trim());
  if (nameFilter) params.set('nameFilter', '1');
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  const body = await apiFetch(`/prospects/entreprise-search?${params.toString()}`);
  return body?.data ?? null;
}

export async function validateCallProduct(prospectId, payload) {
  return apiFetch(`/prospects/${prospectId}/validate-product`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function sendProspectMail(prospectId, payload) {
  return apiFetch(`/prospects/${prospectId}/send-mail`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function searchOdintProspect({
  company,
  postalCode,
  city,
  sirenSiret,
} = {}) {
  return apiFetch('/prospects/odint/search', {
    method: 'POST',
    body: JSON.stringify({
      company: String(company || '').trim(),
      postalCode: postalCode ? String(postalCode).trim() : '',
      city: city ? String(city).trim() : '',
      sirenSiret: sirenSiret ? String(sirenSiret).trim() : '',
    }),
  });
}

export async function searchProspectPagesJaunes(prospectId) {
  return apiFetch(`/prospects/${prospectId}/pages-jaunes/search`, {
    method: 'POST',
  });
}

export async function applyProspectPagesJaunes(prospectId, candidate) {
  return apiFetch(`/prospects/${prospectId}/pages-jaunes/apply`, {
    method: 'POST',
    body: JSON.stringify({ candidate }),
  });
}

